import { z } from "zod";
import type {
  AppointmentPaymentStatus,
  AppointmentStatus,
  ApprovalFeeOutcome,
  VisitType,
} from "@/api/enums";
import type { CancellationPolicyPreview } from "./appointments.contract";
import type { Endpoint } from "./types";

// ── Zod Schemas ─────────────────────────────────────────────────────

const customerProfileInnerSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
  dateOfBirth: z.string().nullable().optional(),
  emailMarketingOptIn: z.boolean().optional(),
  smsOptIn: z.boolean().optional(),
});

export const customerProfileResponseSchema = z
  .object({
    success: z.boolean().optional(),
    message: z.string().optional(),
    data: customerProfileInnerSchema,
  })
  .transform((r) => r.data);

const paymentMethodSummaryInnerSchema = z.object({
  hasPaymentMethod: z.boolean(),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expMonth: z.number().nullable(),
  expYear: z.number().nullable(),
});

export const paymentMethodSummaryResponseSchema = z
  .object({
    success: z.boolean().optional(),
    message: z.string().optional(),
    data: paymentMethodSummaryInnerSchema,
  })
  .transform((r) => r.data);

const setupIntentInnerSchema = z.object({
  clientSecret: z.string(),
});

export const setupIntentResponseSchema = z
  .object({
    success: z.boolean().optional(),
    message: z.string().optional(),
    data: setupIntentInnerSchema,
  })
  .transform((r) => r.data);

// ── Types ──────────────────────────────────────────────────────────

export type CustomerProfile = z.infer<typeof customerProfileResponseSchema>;

export type PaymentMethodSummary = z.infer<
  typeof paymentMethodSummaryResponseSchema
>;

export type SetupIntentResponse = z.infer<typeof setupIntentResponseSchema>;

export interface UpdateCustomerProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface PatientAppointmentsRequest {
  status?: AppointmentStatus;
  take?: number;
  skip?: number;
}

/** The refund fields the patient side is allowed to see. */
interface PatientRefundState {
  approvalFeeOutcome: ApprovalFeeOutcome;
  approvalRefundedCents: number;
  approvalRefundedOnUtc: string | null;
  /**
   * What cancelling right now would do to the fee. Read this rather than
   * comparing `startTime` against the clock: the API decides, and the two can
   * disagree over skew and timezones at exactly the moment it matters.
   */
  cancellationPolicy: CancellationPolicyPreview;
}

export interface PatientAppointmentRecord extends PatientRefundState {
  id: string;
  specialistName: string;
  practiceName: string;
  requestedTime: string | null;
  startTime: string;
  status: AppointmentStatus;
  type: VisitType;
  paymentStatus: AppointmentPaymentStatus;
  durationInMinutes: number;
}

export interface PatientAppointmentDetail extends PatientRefundState {
  id: string;
  specialistName: string;
  symptoms: string;
  startTime: string;
  status: AppointmentStatus;
  visitType: VisitType;
  paymentStatus: AppointmentPaymentStatus;
  durationInMinutes: number;
  practiceName: string;
  locationId?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  locationAddress2?: string | null;
  locationCity?: string | null;
  locationState?: string | null;
  locationZip?: string | null;
}

// ── Endpoints ──────────────────────────────────────────────────────

export const customerEndpoints = {
  profile: { method: "GET", path: "/customer/profile", auth: true, schema: customerProfileResponseSchema } as Endpoint,

  updateProfile: {
    method: "PATCH",
    path: "/customer/profile",
    auth: true,
  } as Endpoint,

  getById: (id: string): Endpoint => ({
    method: "GET",
    path: `/customer/info/${id}`,
    auth: true,
  }),

  appointments: {
    method: "GET",
    path: "/customer/appointments",
    auth: true,
  } as Endpoint,

  appointmentById: (id: string): Endpoint => ({
    method: "GET",
    path: `/customer/appointment/${id}`,
    auth: true,
  }),

  cancelAppointment: {
    method: "PATCH",
    path: "/appointments/cancel-by-customer",
    auth: true,
  } as Endpoint,

  paymentMethod: {
    method: "GET",
    path: "/customer/payment-method",
    auth: true,
    schema: paymentMethodSummaryResponseSchema,
  } as Endpoint,

  createSetupIntent: {
    method: "POST",
    path: "/customer/payment-method/setup-intent",
    auth: true,
    schema: setupIntentResponseSchema,
  } as Endpoint,
} as const;
